import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase";
import { compressImageFile, MAX_UPLOAD_IMAGE_BYTES } from "./imageFile";
import { roomRpc, cacheRoomImage, cloudflareRoomStore } from "./roomTransport";

async function uploadImage(ownerId: string, file: File, destination: Record<string, string>) {
  if (auth.currentUser?.uid !== ownerId) throw new Error("Sign in before uploading an image.");
  // Image decoding can outlive a whole game. Capture its destination before
  // preparing bytes so an old upload cannot consume the next game's quota.
  const room = destination.kind === "truth" ? cloudflareRoomStore(destination.roomCode, ownerId) : undefined;
  const gameNumber = room?.getSnapshot().snapshot?.room.gameNumber ?? 1;
  function requireCurrentGame() {
    if (auth.currentUser?.uid !== ownerId) throw new Error("Sign in before uploading an image.");
    if ((room?.getSnapshot().snapshot?.room.gameNumber ?? 1) !== gameNumber) {
      throw Object.assign(new Error("The game has changed. Choose your image again in the current game."), { code: "functions/failed-precondition" });
    }
  }
  const compressed = await compressImageFile(file);
  if (compressed.size > MAX_UPLOAD_IMAGE_BYTES) throw new Error("Images must be 1 MB or smaller before upload.");
  const bytes = new Uint8Array(await compressed.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  if (destination.kind === "truth") {
    requireCurrentGame();
    const dataUrl = `data:${compressed.type};base64,${btoa(binary)}`;
    const result = await roomRpc<{ url: string }>(destination.roomCode, "uploadRoomImage", { dataUrl, gameNumber });
    requireCurrentGame();
    cacheRoomImage(destination.roomCode, ownerId, result.url, dataUrl, gameNumber);
    return result.url;
  }
  const upload = httpsCallable<Record<string, string>, { url: string }>(functions, "uploadImage");
  const result = await upload({ ...destination, base64: btoa(binary), contentType: compressed.type });
  return result.data.url;
}
export async function uploadTriviaImage(ownerId: string, packId: string, _slot: string, file: File) {
  return uploadImage(ownerId, file, { kind: "trivia", packId });
}
export async function uploadAvatar(ownerId: string, file: File) {
  return uploadImage(ownerId, file, { kind: "avatar" });
}
export async function uploadTruthImage(ownerId: string, roomCode: string, _slot: string, file: File) {
  return uploadImage(ownerId, file, { kind: "truth", roomCode });
}
